class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.closeAnimationTimeout = null;
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    if (!this.mainDetailsToggle.open || this.mainDetailsToggle.classList.contains('is-closing')) return;

    const finishClose = () => {
      clearTimeout(this.closeAnimationTimeout);
      this.closeAnimationTimeout = null;
      this.mainDetailsToggle.classList.remove('is-closing');
      this.mainDetailsToggle.removeAttribute('open');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose();
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== this.content || event.propertyName !== 'opacity') return;
      this.content.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    };

    this.mainDetailsToggle.classList.add('is-closing');
    this.content.addEventListener('transitionend', onTransitionEnd);
    this.closeAnimationTimeout = setTimeout(() => {
      this.content.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    }, 260);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.mql = window.matchMedia('(min-width: 990px) and (hover: hover)');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.closeTimeout = null;
    this.hoverOpenTimeout = null;

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.summary.addEventListener('click', this.onSummaryClick);

    this.setHoverBehavior(this.mql.matches);
    this.mql.addEventListener('change', (event) => this.setHoverBehavior(event.matches));
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  setHoverBehavior(enabled) {
    if (enabled) {
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
    } else {
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
    }
  }

  onMouseEnter() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    if (!this.mainDetailsToggle.open && !this.hoverOpenTimeout) {
      this.hoverOpenTimeout = setTimeout(() => {
        this.hoverOpenTimeout = null;
        this.open();
      }, 120);
    }
  }

  open() {
    if (this.mainDetailsToggle.classList.contains('is-closing')) {
      clearTimeout(this.closeAnimationTimeout);
      this.closeAnimationTimeout = null;
      this.mainDetailsToggle.classList.remove('is-closing');
    }
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
  }

  onMouseLeave() {
    if (this.hoverOpenTimeout) {
      clearTimeout(this.hoverOpenTimeout);
      this.hoverOpenTimeout = null;
    }
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
    if (!this.mainDetailsToggle.open) return;
    this.closeTimeout = setTimeout(() => {
      this.close();
      this.closeTimeout = null;
    }, 220);
  }

  onSummaryClick(event) {
    if (this.mql.matches) {
      event.preventDefault();
      if (this.hoverOpenTimeout) {
        clearTimeout(this.hoverOpenTimeout);
        this.hoverOpenTimeout = null;
      }
      if (this.mainDetailsToggle.open) {
        this.close();
      } else {
        this.open();
      }
    }
  }
}

customElements.define('header-menu', HeaderMenu);
